export const PAGE_RANGE_DIAGNOSTIC_CODES = {
  emptyExpression: 'pageRange.emptyExpression',
  emptySegment: 'pageRange.emptySegment',
  invalidSegment: 'pageRange.invalidSegment',
  keywordMustStandAlone: 'pageRange.keywordMustStandAlone',
  pageBelowMinimum: 'pageRange.pageBelowMinimum',
  pageOutOfRange: 'pageRange.pageOutOfRange',
  reversedRange: 'pageRange.reversedRange',
  repeatedSelection: 'pageRange.repeatedSelection',
  overlappingSelection: 'pageRange.overlappingSelection',
} as const;

export type PageRangeDiagnosticCode =
  (typeof PAGE_RANGE_DIAGNOSTIC_CODES)[keyof typeof PAGE_RANGE_DIAGNOSTIC_CODES];

export interface PageRangeDiagnostic {
  readonly code: PageRangeDiagnosticCode;
  readonly segmentIndex: number;
  readonly segment: string;
  readonly page?: number;
  readonly start?: number;
  readonly end?: number;
  readonly previousSegmentIndex?: number;
}

export interface PageRangeParseResult {
  readonly ok: boolean;
  /** Unique, one-based pages in the order in which the user selected them. */
  readonly pages: readonly number[];
  /** ASCII representation suitable for storage, never for direct display as an error. */
  readonly normalized: string;
  readonly diagnostics: readonly PageRangeDiagnostic[];
}

type Selection =
  | { readonly kind: 'page'; readonly start: number; readonly end: number }
  | { readonly kind: 'range'; readonly start: number; readonly end: number };

const ARABIC_INDIC_ZERO = '٠'.codePointAt(0) ?? 0;
const EASTERN_ARABIC_INDIC_ZERO = '۰'.codePointAt(0) ?? 0;
const ASCII_ZERO = '0'.codePointAt(0) ?? 0;

const KEYWORDS = new Map<string, 'all' | 'odd' | 'even'>([
  ['all', 'all'],
  ['odd', 'odd'],
  ['even', 'even'],
  ['الكل', 'all'],
  ['فردي', 'odd'],
  ['زوجي', 'even'],
]);

function normalizeDigit(character: string): string {
  const codePoint = character.codePointAt(0);
  if (codePoint === undefined) return character;

  if (codePoint >= ARABIC_INDIC_ZERO && codePoint <= ARABIC_INDIC_ZERO + 9) {
    return String.fromCodePoint(ASCII_ZERO + codePoint - ARABIC_INDIC_ZERO);
  }

  if (
    codePoint >= EASTERN_ARABIC_INDIC_ZERO &&
    codePoint <= EASTERN_ARABIC_INDIC_ZERO + 9
  ) {
    return String.fromCodePoint(
      ASCII_ZERO + codePoint - EASTERN_ARABIC_INDIC_ZERO,
    );
  }

  return character;
}

/**
 * Normalizes characters users commonly enter from Arabic and mobile keyboards.
 * It deliberately does not translate free-form words beyond the supported keywords.
 */
export function normalizePageRangeInput(input: string): string {
  return Array.from(input.normalize('NFKC'), normalizeDigit)
    .join('')
    .replaceAll('،', ',')
    .replace(/[\u2012\u2013\u2014\u2212]/gu, '-');
}

function diagnostic(
  code: PageRangeDiagnosticCode,
  segmentIndex: number,
  segment: string,
  details: Omit<PageRangeDiagnostic, 'code' | 'segmentIndex' | 'segment'> = {},
): PageRangeDiagnostic {
  return { code, segmentIndex, segment, ...details };
}

function parseSelection(
  segment: string,
  segmentIndex: number,
  pageCount: number,
): { readonly selection?: Selection; readonly diagnostics: readonly PageRangeDiagnostic[] } {
  const compact = segment.replace(/\s+/gu, '');
  const match = /^(\d+)(?:-(\d+))?$/u.exec(compact);
  if (!match) {
    return {
      diagnostics: [
        diagnostic(
          PAGE_RANGE_DIAGNOSTIC_CODES.invalidSegment,
          segmentIndex,
          segment,
        ),
      ],
    };
  }

  const startText = match[1];
  const endText = match[2];
  if (startText === undefined) {
    return {
      diagnostics: [
        diagnostic(
          PAGE_RANGE_DIAGNOSTIC_CODES.invalidSegment,
          segmentIndex,
          segment,
        ),
      ],
    };
  }

  const start = Number(startText);
  const end = endText === undefined ? start : Number(endText);
  const diagnostics: PageRangeDiagnostic[] = [];

  if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end)) {
    diagnostics.push(
      diagnostic(
        PAGE_RANGE_DIAGNOSTIC_CODES.invalidSegment,
        segmentIndex,
        segment,
      ),
    );
    return { diagnostics };
  }

  if (start < 1) {
    diagnostics.push(
      diagnostic(
        PAGE_RANGE_DIAGNOSTIC_CODES.pageBelowMinimum,
        segmentIndex,
        segment,
        { page: start },
      ),
    );
  }
  if (end < 1 && end !== start) {
    diagnostics.push(
      diagnostic(
        PAGE_RANGE_DIAGNOSTIC_CODES.pageBelowMinimum,
        segmentIndex,
        segment,
        { page: end },
      ),
    );
  }
  if (start > pageCount) {
    diagnostics.push(
      diagnostic(
        PAGE_RANGE_DIAGNOSTIC_CODES.pageOutOfRange,
        segmentIndex,
        segment,
        { page: start },
      ),
    );
  }
  if (end > pageCount && end !== start) {
    diagnostics.push(
      diagnostic(
        PAGE_RANGE_DIAGNOSTIC_CODES.pageOutOfRange,
        segmentIndex,
        segment,
        { page: end },
      ),
    );
  }
  if (start > end) {
    diagnostics.push(
      diagnostic(
        PAGE_RANGE_DIAGNOSTIC_CODES.reversedRange,
        segmentIndex,
        segment,
        { start, end },
      ),
    );
  }

  if (diagnostics.length > 0) return { diagnostics };

  return {
    selection: {
      kind: endText === undefined ? 'page' : 'range',
      start,
      end,
    },
    diagnostics,
  };
}

function pagesForKeyword(
  keyword: 'all' | 'odd' | 'even',
  pageCount: number,
): readonly number[] {
  const firstPage = keyword === 'even' ? 2 : 1;
  const increment = keyword === 'all' ? 1 : 2;
  const pages: number[] = [];
  for (let page = firstPage; page <= pageCount; page += increment) pages.push(page);
  return pages;
}

/**
 * Parses a strict, one-based page selection.
 *
 * Overlaps and repeated clauses are errors rather than being silently de-duplicated.
 * `pages` still contains the unique, valid pages parsed before and after a diagnostic so
 * callers can render a useful preview, but processing must only continue when `ok` is true.
 */
export function parsePageRange(input: string, pageCount: number): PageRangeParseResult {
  if (!Number.isSafeInteger(pageCount) || pageCount < 1) {
    throw new RangeError('pageCount must be a positive safe integer');
  }

  const normalizedInput = normalizePageRangeInput(input).trim();
  if (normalizedInput.length === 0) {
    return {
      ok: false,
      pages: [],
      normalized: '',
      diagnostics: [
        diagnostic(PAGE_RANGE_DIAGNOSTIC_CODES.emptyExpression, 0, ''),
      ],
    };
  }

  const segments = normalizedInput.split(',');
  const normalizedSegments = segments.map((segment) => segment.trim().toLocaleLowerCase('en'));
  const keywordSegments = normalizedSegments
    .map((segment, index) => ({ keyword: KEYWORDS.get(segment), index, segment }))
    .filter(
      (candidate): candidate is {
        readonly keyword: 'all' | 'odd' | 'even';
        readonly index: number;
        readonly segment: string;
      } => candidate.keyword !== undefined,
    );

  if (keywordSegments.length > 0) {
    if (segments.length !== 1 || keywordSegments.length !== 1) {
      const diagnostics = keywordSegments.map(({ index, segment }) =>
        diagnostic(
          PAGE_RANGE_DIAGNOSTIC_CODES.keywordMustStandAlone,
          index,
          segment,
        ),
      );
      return { ok: false, pages: [], normalized: normalizedInput, diagnostics };
    }

    const keyword = keywordSegments[0]?.keyword;
    if (keyword === undefined) {
      return { ok: false, pages: [], normalized: normalizedInput, diagnostics: [] };
    }
    return {
      ok: true,
      pages: pagesForKeyword(keyword, pageCount),
      normalized: keyword,
      diagnostics: [],
    };
  }

  const diagnostics: PageRangeDiagnostic[] = [];
  const pages: number[] = [];
  const claimedBy = new Map<number, number>();
  const selections: Array<Selection | undefined> = [];

  segments.forEach((rawSegment, segmentIndex) => {
    const segment = rawSegment.trim();
    if (segment.length === 0) {
      diagnostics.push(
        diagnostic(
          PAGE_RANGE_DIAGNOSTIC_CODES.emptySegment,
          segmentIndex,
          segment,
        ),
      );
      selections.push(undefined);
      return;
    }

    const parsed = parseSelection(segment, segmentIndex, pageCount);
    diagnostics.push(...parsed.diagnostics);
    selections.push(parsed.selection);
    if (parsed.selection === undefined) return;

    const duplicateOwners = new Set<number>();
    for (let page = parsed.selection.start; page <= parsed.selection.end; page += 1) {
      const owner = claimedBy.get(page);
      if (owner === undefined) {
        claimedBy.set(page, segmentIndex);
        pages.push(page);
      } else {
        duplicateOwners.add(owner);
      }
    }

    for (const previousSegmentIndex of duplicateOwners) {
      const previousSelection = selections[previousSegmentIndex];
      const isExactRepeat =
        previousSelection !== undefined &&
        previousSelection.start === parsed.selection.start &&
        previousSelection.end === parsed.selection.end;
      diagnostics.push(
        diagnostic(
          isExactRepeat
            ? PAGE_RANGE_DIAGNOSTIC_CODES.repeatedSelection
            : PAGE_RANGE_DIAGNOSTIC_CODES.overlappingSelection,
          segmentIndex,
          segment,
          {
            start: parsed.selection.start,
            end: parsed.selection.end,
            previousSegmentIndex,
          },
        ),
      );
    }
  });

  const normalized = selections
    .map((selection, index) => {
      if (selection === undefined) return segments[index]?.trim() ?? '';
      return selection.kind === 'page'
        ? String(selection.start)
        : `${selection.start}-${selection.end}`;
    })
    .join(',');

  return {
    ok: diagnostics.length === 0,
    pages,
    normalized,
    diagnostics,
  };
}

